import React, { useEffect, useState } from "react";
import api, { formatApiError, money } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import { toast } from "sonner";

const pendingOf = (it) => Number(it.qty_pending != null ? it.qty_pending : it.qty) || 0;

export const ConvertToOrderDialog = ({ open, onOpenChange, doc, endpoint, onDone, kind = "order" }) => {
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [days, setDays] = useState("3");
  const isDel = kind === "delivery";
  const noun = isDel ? "nota de envío" : "pedido";

  useEffect(() => {
    if (!open || !doc) return;
    const items = (doc.items || []).map((it, index) => ({
      index, description: it.description, unit: it.unit || "",
      unit_price: Number(it.unit_price) || 0, pending: pendingOf(it),
    })).filter((r) => r.pending > 0);
    setRows(items.map((r) => ({ ...r, checked: true, qty: String(r.pending) })));
  }, [open, doc]);

  const toggle = (i, v) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, checked: v } : r));
  const setQty = (i, v) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, qty: v } : r));

  const selected = rows.filter((r) => r.checked && Number(r.qty) > 0);
  const total = selected.reduce((s, r) => s + Math.min(Number(r.qty) || 0, r.pending) * r.unit_price, 0);
  const cur = doc?.currency || "GTQ";

  const submit = async () => {
    for (const r of selected) {
      if (Number(r.qty) > r.pending + 1e-6) { toast.error(`"${r.description}": máximo ${r.pending}`); return; }
    }
    if (selected.length === 0) { toast.error("Selecciona al menos un ítem con cantidad"); return; }
    setSaving(true);
    try {
      const body = { items: selected.map((r) => ({ index: r.index, qty: Number(r.qty) })) };
      const res = await api.post(isDel ? `${endpoint}?follow_up_days=${parseInt(days, 10) || 0}` : endpoint, body);
      toast.success(isDel ? `Nota de envío ${res.data.number} creada` : `Pedido ${res.data.number} creado`);
      onOpenChange(false);
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onOpenChange(false); }}>
      <DialogContent className="rounded-sm max-w-2xl" data-testid="convert-order-dialog"
        onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-heading">Convertir a {noun} — {doc?.number}</DialogTitle>
          <DialogDescription>Selecciona los ítems y la cantidad {isDel ? "a enviar" : "autorizada"}. Lo demás queda pendiente para convertir después.</DialogDescription>
        </DialogHeader>
        {rows.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">No hay ítems pendientes por convertir.</div>
        ) : (
          <div className="max-h-[50vh] overflow-y-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b">
                <th className="py-2 w-8"></th><th className="py-2">Descripción</th>
                <th className="py-2 text-right">Pendiente</th><th className="py-2 text-right w-28">{isDel ? "Enviar" : "Autorizar"}</th>
                <th className="py-2 text-right">Subtotal</th></tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.index} className={`border-b last:border-0 ${r.checked ? "" : "opacity-40"}`} data-testid={`convert-item-${r.index}`}>
                    <td className="py-2"><Checkbox checked={r.checked} onCheckedChange={(v) => toggle(i, !!v)} data-testid={`convert-check-${r.index}`} /></td>
                    <td className="py-2 pr-2">{r.description}<span className="text-xs text-muted-foreground"> · {money(cur, r.unit_price)}</span></td>
                    <td className="py-2 text-right font-mono">{r.pending}</td>
                    <td className="py-2 text-right">
                      <Input type="number" min="0" step="any" max={r.pending} value={r.qty} disabled={!r.checked}
                        onChange={(e) => setQty(i, e.target.value)} className="h-8 rounded-sm text-right" data-testid={`convert-qty-${r.index}`} />
                    </td>
                    <td className="py-2 text-right font-mono">{money(cur, r.checked ? Math.min(Number(r.qty) || 0, r.pending) * r.unit_price : 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {isDel && (
          <label className="flex items-center gap-2 text-sm">Agendar seguimiento en
            <Input type="number" min="0" value={days} onChange={(e) => setDays(e.target.value)} className="h-8 w-20 rounded-sm" data-testid="convert-followup-days" /> días (0 = ninguno)</label>
        )}
        <DialogFooter className="items-center">
          <div className="mr-auto text-sm font-semibold" data-testid="convert-total">Total: {money(cur, total)}</div>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button className="rounded-sm font-bold" onClick={submit} disabled={saving || selected.length === 0} data-testid="confirm-convert-order">
            {saving ? "Creando..." : `Crear ${noun}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
